import { motion } from 'framer-motion'
import { CalendarIcon } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { ViewTransitionLink } from '#app/components/view-transition-link.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta } from '#app/utils/meta.ts'
import { useNavigationTheme } from '#app/utils/navigation-theme-context.tsx'
import { type Route } from './+types/_marketing.nieuws.index.ts'

export const meta: Route.MetaFunction = ({ data }) => {
	const title = data?.page?.metaTitle || data?.page?.title || 'Nieuws'
	const description = data?.page?.metaDescription || 'Lees het laatste nieuws en inzichten van Ignite Identity over branding, design en identiteit.'
	return buildMeta({
		title,
		description,
		url: '/nieuws',
	})
}

export async function loader() {
	// First check if there's a dynamic page with slug "nieuws"
	const page = await prisma.page.findUnique({
		where: { slug: 'nieuws', published: true },
		include: {
			blocks: {
				orderBy: { order: 'asc' },
			},
		},
	})

	// Fetch news articles for both dynamic and fallback rendering
	const articles = await prisma.newsArticle.findMany({
		where: { published: true },
		select: {
			id: true,
			slug: true,
			title: true,
			excerpt: true,
			featuredImage: true,
			imageAlt: true,
			category: true,
			author: true,
			publishedAt: true,
			heroVideoMode: true,
			heroVideoPlaybackId: true,
		},
		orderBy: { publishedAt: 'desc' },
	})

	// Map to format expected by BlockRenderer
	const newsArticles = articles.map((a) => ({
		id: a.id,
		title: a.title,
		slug: a.slug,
		excerpt: a.excerpt,
		imageUrl: a.featuredImage,
		category: a.category,
		publishedAt: a.publishedAt?.toISOString() || null,
		videoMode: (a.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
		videoPlaybackId: a.heroVideoPlaybackId ?? undefined,
	}))

	// Get unique categories for filtering
	const categories = [...new Set(articles.map((a) => a.category).filter(Boolean))]

	// If there's a dynamic page, return it along with news articles for the block
	if (page) {
		// Also fetch services and case studies in case other blocks need them
		const [services, caseStudies] = await Promise.all([
			prisma.service.findMany({
				select: { id: true, name: true, slug: true, description: true },
				orderBy: { name: 'asc' },
			}),
			prisma.caseStudy.findMany({
				where: { published: true },
				select: {
					id: true,
					title: true,
					slug: true,
					client: true,
					imageUrl: true,
					heroVideoMode: true,
					heroVideoPlaybackId: true,
				},
				orderBy: { order: 'asc' },
				take: 6,
			}),
		])

		return {
			page,
			services,
			caseStudies: caseStudies.map((cs) => ({
				...cs,
				videoMode: (cs.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
				videoPlaybackId: cs.heroVideoPlaybackId ?? undefined,
			})),
			newsArticles,
			articles,
			categories,
			useDynamicPage: true,
			ctaConfig: {
				mode: page.ctaMode,
				title: page.ctaTitle,
				buttonText: page.ctaButtonText,
				buttonLink: page.ctaButtonLink,
				imageUrl: page.ctaImageUrl,
				imageAlt: page.ctaImageAlt,
				highlightWords: page.ctaHighlightWords,
				fontSizePreset: page.ctaFontSizePreset,
				showButton: page.ctaShowButton,
			},
		}
	}

	return { page: null, services: [], caseStudies: [], newsArticles, articles, categories, useDynamicPage: false, ctaConfig: null }
}

// Animation variants
const fadeIn = {
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
}

const staggerChildren = {
	animate: {
		transition: {
			staggerChildren: 0.12,
		},
	},
}

export default function NewsIndex({ loaderData }: Route.ComponentProps) {
	const { page, services, caseStudies, newsArticles, articles, useDynamicPage } = loaderData
	const { setNavigationTheme } = useNavigationTheme()

	// If there's a dynamic page, render it using BlockRenderer
	if (useDynamicPage && page) {
		return (
			<main>
				<BlockRenderer
					blocks={page.blocks}
					services={services}
					caseStudies={caseStudies}
					newsArticles={newsArticles}
				/>
			</main>
		)
	}

	// Set navigation theme for dark hero (fallback layout)
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useEffect(() => {
		setNavigationTheme(true, '#1a1a1a')
		return () => {
			setNavigationTheme(false, 'transparent')
		}
	}, [setNavigationTheme])

	// Separate featured (first) article from the rest
	const [featuredArticle, ...otherArticles] = articles

	return (
		<motion.main
			className="bg-white"
			initial="initial"
			animate="animate"
		>
			{/* Hero Section */}
			<section className="relative bg-[#1a1a1a] pt-32 pb-16 md:pb-24">
				{/* Breadcrumb Navigation */}
				<motion.nav
					className="relative z-10 px-6 py-4 md:px-12 lg:px-24 max-w-7xl mx-auto"
					variants={fadeIn}
				>
					<div className="flex items-center gap-2 text-sm text-white/60">
						<ViewTransitionLink to="/" className="hover:text-white transition-colors">
							Home
						</ViewTransitionLink>
						<span>/</span>
						<span className="text-white">Nieuws</span>
					</div>
				</motion.nav>

				<div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
					<motion.h1
						className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6"
						variants={fadeIn}
					>
						Nieuws
					</motion.h1>
					<motion.p
						className="text-lg md:text-xl text-white/70 max-w-2xl"
						variants={fadeIn}
					>
						Het laatste nieuws en inzichten over branding, design en identiteit.
					</motion.p>
				</div>
			</section>

			{/* Articles Grid */}
			<section className="px-6 py-16 md:px-12 md:py-24 lg:px-24">
				<div className="max-w-7xl mx-auto">
					{articles.length === 0 ? (
						<motion.div
							className="text-center py-16"
							variants={fadeIn}
						>
							<p className="text-lg text-gray-600">
								Er zijn nog geen nieuwsartikelen gepubliceerd.
							</p>
						</motion.div>
					) : (
						<motion.div variants={staggerChildren}>
							{/* Featured Article */}
							{featuredArticle && (
								<motion.article
									className="mb-16"
									variants={fadeIn}
								>
									<Link
										to={`/nieuws/${featuredArticle.slug}`}
										className="group block"
									>
										<div className="grid md:grid-cols-2 gap-8 items-center">
											<div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-gray-200">
												{featuredArticle.featuredImage && (
													<img
														src={featuredArticle.featuredImage}
														alt={featuredArticle.imageAlt || featuredArticle.title}
														className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
													/>
												)}
											</div>
											<div>
												{featuredArticle.category && (
													<span className="inline-block px-3 py-1 text-xs font-medium text-primary bg-primary/10 rounded-full mb-4">
														{featuredArticle.category}
													</span>
												)}
												<h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 group-hover:text-primary transition-colors mb-4">
													{featuredArticle.title}
												</h2>
												{featuredArticle.excerpt && (
													<p className="text-gray-600 text-lg mb-4 line-clamp-3">
														{featuredArticle.excerpt}
													</p>
												)}
												<div className="flex items-center gap-4 text-sm text-gray-500">
													{featuredArticle.author && (
														<span>{featuredArticle.author}</span>
													)}
													{featuredArticle.publishedAt && (
														<div className="flex items-center gap-1">
															<CalendarIcon className="w-4 h-4" />
															<time>
																{new Date(featuredArticle.publishedAt).toLocaleDateString('nl-NL', {
																	year: 'numeric',
																	month: 'long',
																	day: 'numeric',
																})}
															</time>
														</div>
													)}
												</div>
											</div>
										</div>
									</Link>
								</motion.article>
							)}

							{/* Divider */}
							{otherArticles.length > 0 && (
								<div className="border-t border-gray-200 mb-12" />
							)}

							{/* Other Articles Grid */}
							{otherArticles.length > 0 && (
								<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
									{otherArticles.map((article) => {
										const formattedDate = article.publishedAt
											? new Date(article.publishedAt).toLocaleDateString('nl-NL', {
													year: 'numeric',
													month: 'short',
													day: 'numeric',
												})
											: null

										return (
											<motion.article
												key={article.id}
												className="group"
												variants={fadeIn}
												whileHover={{ y: -4 }}
												transition={{ duration: 0.2 }}
											>
												<Link
													to={`/nieuws/${article.slug}`}
													className="block"
												>
													<div className="relative aspect-[16/9] rounded-lg overflow-hidden mb-4 bg-gray-200">
														{article.featuredImage && (
															<img
																src={article.featuredImage}
																alt={article.imageAlt || article.title}
																className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
															/>
														)}
													</div>

													{article.category && (
														<span className="text-xs font-medium text-primary mb-2 block">
															{article.category}
														</span>
													)}

													<h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors mb-2">
														{article.title}
													</h3>

													{article.excerpt && (
														<p className="text-sm text-gray-600 line-clamp-2 mb-3">
															{article.excerpt}
														</p>
													)}

													{formattedDate && (
														<time className="text-xs text-gray-500">
															{formattedDate}
														</time>
													)}
												</Link>
											</motion.article>
										)
									})}
								</div>
							)}
						</motion.div>
					)}
				</div>
			</section>
		</motion.main>
	)
}
